import { EditOutlined, FilePdfOutlined } from '@ant-design/icons'
import type { Home } from '@lmc/ts-common'
import {
  Button,
  FilterContainer,
  PageHeader,
  RangePicker,
  Spinner
} from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import ExternalLink from 'components/icons/ExternalLink'
import dayjs from 'dayjs'
import usePdfExport from 'hooks/usePdfExport'
import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { AdministrationRecordDrawer } from '../componentsV2/AdministrationRecordDrawer'
import { DiscontinuedRecordDrawer } from '../componentsV2/DiscontinuedRecordDrawer'
import AddStockModal from '../componentsV2/modalsV2/AddStockModal'
import AdministrationModal from '../componentsV2/modalsV2/AdministrationModal'
import DisposalModal from '../componentsV2/modalsV2/DisposalModal'
import StockCheckModal from '../componentsV2/modalsV2/StockCheckModal'
import PageWrapper from '../componentsV2/PageWrapper'
import { StockRecordDrawer } from '../componentsV2/StockRecordDrawer/StockRecordDrawer'
import Legend from '../EMARResidentScreenV2/Legend'
import MedicationsTable from '../EMARResidentScreenV2/MedicationsTable'
import { useCurrentStock } from '../hooks/useCurrentStock'
import { useMarChart } from '../hooks/useMarChart'
import { useMedicationSchedulePattern } from '../hooks/useMedicationSchedulePattern'
import { useStockRecords } from '../hooks/useStockRecords'
import { MedicationStockReason, type StockRecord } from '../types'
import { transformMarChartData } from '../utils/administrationUtils'
import { getMedicationIcon } from '../utils/medicationUtils'
import RecordsSection from './RecordsSection'

interface MedicationInformationScreenV2Props {
  testId?: string
}

const MedicationInformationScreenV2 = ({
  testId
}: MedicationInformationScreenV2Props = {}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const { id: residentId, medication_id: medicationId } = useParams<{
    id: string
    medication_id: string
  }>()
  const navigate = useNavigate()
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const { resident, isLoading: isLoadingResident } = useResident({
    id: residentId
  })

  const [startDate, setStartDate] = useState(
    dayjs().subtract(14, 'days').startOf('day')
  )
  const [endDate, setEndDate] = useState(dayjs().endOf('day'))

  const { data: medicationPattern, isLoading: isLoadingMedication } =
    useMedicationSchedulePattern({
      medicationId: medicationId || '',
      enabled: !!medicationId
    })

  const { records, isLoading: isLoadingStockRecords } = useStockRecords({
    medicationId: medicationId || '',
    startDate,
    endDate,
    includeChartData: false
  })

  const { currentStock, isLoading: isLoadingCurrentStock } = useCurrentStock({
    residentMedicationId: medicationId || '',
    residentId: residentId || '',
    enabled: !!medicationId && !!residentId
  })

  const { data: marChartData, isLoading: isLoadingMarChart } = useMarChart({
    residentId: residentId || '',
    startDate,
    endDate,
    enabled: !!residentId
  })

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [administrationDrawerOpen, setAdministrationDrawerOpen] =
    useState(false)
  const [discontinuedDrawerOpen, setDiscontinuedDrawerOpen] = useState(false)
  const [cellDrawerOpen, setCellDrawerOpen] = useState(false)
  const [cellDrawerRecord, setCellDrawerRecord] = useState<
    StockRecord | undefined
  >(undefined)

  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [stockCheckModalOpen, setStockCheckModalOpen] = useState(false)
  const [addStockModalOpen, setAddStockModalOpen] = useState(false)
  const [disposalModalOpen, setDisposalModalOpen] = useState(false)
  const [administrationModalOpen, setAdministrationModalOpen] = useState(false)
  const [editingRecord, setEditingRecord] = useState<StockRecord | null>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const pdfContentRef = useRef<HTMLDivElement>(null)

  const medicationType = useMemo(() => {
    if (!medicationPattern) return 'scheduled'
    return medicationPattern.prn ? 'prn' : 'scheduled'
  }, [medicationPattern])

  const isDiscontinued = useMemo(() => {
    if (!medicationPattern) return false
    return medicationPattern.status?.toLowerCase() === 'discontinued'
  }, [medicationPattern])

  const filteredMarChartData = useMemo(() => {
    if (!marChartData || !medicationId) return null
    return {
      ...marChartData,
      medications:
        marChartData.medications?.filter(
          (med: any) => med.residentMedicationId === medicationId
        ) || []
    }
  }, [marChartData, medicationId])

  const tableData = useMemo(() => {
    return transformMarChartData(
      filteredMarChartData,
      medicationType,
      startDate,
      endDate
    )
  }, [filteredMarChartData, medicationType, startDate, endDate])

  const residentName = resident
    ? `${resident.name.first} ${resident.name.last}`
    : ''

  const handleDateChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].startOf('day'))
      setEndDate(dates[1].endOf('day'))
    } else {
      setStartDate(dayjs().subtract(14, 'days').startOf('day'))
      setEndDate(dayjs().endOf('day'))
    }
  }

  const handleViewRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDrawerOpen(true)
  }

  const handleViewAdministrationRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setAdministrationDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleCloseAdministrationDrawer = () => {
    setAdministrationDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleViewDiscontinuedRecord = (recordId: string) => {
    setSelectedRecordId(recordId)
    setDiscontinuedDrawerOpen(true)
  }

  const handleCloseDiscontinuedDrawer = () => {
    setDiscontinuedDrawerOpen(false)
    setSelectedRecordId(null)
  }

  const handleCellClick = (record: any, dateKey: string) => {
    const cellData = record[dateKey]
    if (!cellData?.outcomes || cellData.outcomes.length === 0) return

    const firstOutcome = cellData.outcomes[0]
    if (firstOutcome.action === 'MISSED' || firstOutcome.action === 'PAUSED') {
      return
    }

    const administration = firstOutcome.administration
    const dateStatus = firstOutcome.dateStatus

    if (!administration) return

    const recordedBy = administration.recordedBy || {}
    const recordedByName =
      typeof recordedBy.name === 'object'
        ? `${recordedBy.name.first} ${recordedBy.name.last}`
        : recordedBy.name || ''

    const medication = record.medication
    const medicationData = administration.medication || {}
    const adminData = administration.administration || {}

    const stockRecord: StockRecord = {
      id: administration._id,
      date:
        administration.date || administration.actionTime || dateStatus?.date,
      actionTime:
        administration.actionTime || administration.date || dateStatus?.date,
      action: MedicationStockReason.MED_ADMIN,
      type: 'administration',
      administrationStatus: (adminData.status ||
        dateStatus?.status ||
        administration.administrationStatus) as any,
      recordedBy: {
        id: recordedBy.id || '',
        name: recordedByName,
        picture: recordedBy.picture || undefined
      },
      notes: administration.notes || [],
      quantityChange:
        administration.stock?.quantityChange ??
        (medicationData.doseQuantity
          ? -medicationData.doseQuantity
          : dateStatus?.doseQuantity
            ? -dateStatus.doseQuantity
            : 0),
      unit: dateStatus?.doseUnit || medicationData.doseUnit || '',
      witnessed: administration.witnessed || false,
      witnesses: administration.witnesses || [],
      expectedStock: medication?.currentStock || 0,
      stockCount: medication?.currentStock || 0,
      changelog: administration.changelog || [],
      administration: {
        ...administration,
        doseQuantity:
          medicationData.doseQuantity || dateStatus?.doseQuantity || 0,
        dose: medicationData.doseQuantity || dateStatus?.doseQuantity || 0,
        recordedBy: {
          id: recordedBy.id || '',
          name:
            typeof recordedBy.name === 'object'
              ? recordedBy.name
              : { first: '', last: recordedBy.name || '' },
          picture: recordedBy.picture || undefined
        },
        administrationStatus: (adminData.status ||
          dateStatus?.status ||
          administration.administrationStatus) as any
      } as any,
      stockChanged:
        adminData.stockAffected === false ? false : dateStatus?.stockChanged,
      altDose: medicationData.altDose || undefined
    } as StockRecord

    setCellDrawerRecord(stockRecord)
    setCellDrawerOpen(true)
  }

  const handleCloseCellDrawer = () => {
    setCellDrawerOpen(false)
    setCellDrawerRecord(undefined)
  }

  const handleEditFromDrawer = (
    record: StockRecord,
    actionType?: MedicationStockReason
  ) => {
    if (record.type === 'administration') {
      setIsEditMode(true)
      setEditingRecord(record)
      setAdministrationModalOpen(true)
      return
    }

    if (!actionType) return

    setIsEditMode(true)
    setEditingRecord(record)

    switch (actionType) {
      case MedicationStockReason.STOCK_COUNT:
      case MedicationStockReason.RECONCILIATION:
      case MedicationStockReason.INITIAL_STOCK:
        setStockCheckModalOpen(true)
        break
      case MedicationStockReason.RESTOCK:
        setAddStockModalOpen(true)
        break
      case MedicationStockReason.DISPOSAL:
        setDisposalModalOpen(true)
        break
      default:
        break
    }
  }

  const handleStockCheckSuccess = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleStockCheckCancel = () => {
    setStockCheckModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockSuccess = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleAddStockCancel = () => {
    setAddStockModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalSuccess = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleDisposalCancel = () => {
    setDisposalModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationCancel = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const handleEditAdministrationSuccess = () => {
    setAdministrationModalOpen(false)
    setIsEditMode(false)
    setEditingRecord(null)
  }

  const expectedStock = currentStock?.currentStockLevel ?? 0

  const handleEditConfiguration = () => {
    navigate(`/residents/${residentId}/emar/${medicationId}/edit`)
  }

  const pdfTitle = `${residentName} - ${medicationPattern?.medicationName || ''} - ${startDate.format('DD-MM-YYYY')} to ${endDate.format('DD-MM-YYYY')}`
  const handleDownloadPdf = usePdfExport(
    pdfContentRef as React.RefObject<HTMLInputElement>,
    pdfTitle,
    setIsDownloading
  )

  if (
    isLoadingResident ||
    !resident ||
    isLoadingMedication ||
    isLoadingStockRecords ||
    isLoadingCurrentStock
  ) {
    return (
      <PageWrapper>
        <Spinner style={{ marginTop: '80px' }} />
      </PageWrapper>
    )
  }

  if (!medicationPattern) {
    return (
      <PageWrapper>
        <Text>{t('MEDICATION_NOT_FOUND')}</Text>
      </PageWrapper>
    )
  }

  return (
    <>
      <PageHeader
        onBack={() => navigate(-1)}
        title={
          <HeaderTitleContainer>
            {getMedicationIcon(medicationPattern?.status, 'large')}
            <MedicationTitleContainer>
              <HeadingStyled level={1}>
                {medicationPattern?.medicationName}
              </HeadingStyled>
              {isDiscontinued && (
                <InfoText>
                  {t('DISCONTINUED_SINCE', {
                    date: dayjs(
                      medicationPattern?.discontinued?.at ||
                        medicationPattern?.endDate
                    ).format('DD-MM-YYYY')
                  })}
                </InfoText>
              )}
            </MedicationTitleContainer>
          </HeaderTitleContainer>
        }
        sticky
      >
        <HeaderButtonContainer>
          <ReadMoreLink
            href="https://support.logmycare.co.uk/en/collections/2749236-emar"
            target="_blank"
            rel="noreferrer"
          >
            {t('READ_MORE')} <ExternalLink />
          </ReadMoreLink>
          <Button
            onClick={handleEditConfiguration}
            variant="default"
            style={{ whiteSpace: 'nowrap' }}
          >
            <EditOutlined />
            {t('CONFIGURATION')}
          </Button>
          <Button
            onClick={handleDownloadPdf}
            variant="default"
            disabled={isDownloading}
            isLoading={isDownloading}
            style={{ whiteSpace: 'nowrap' }}
          >
            <FilePdfOutlined />
            {t('DOWNLOAD_PDF')}
          </Button>
        </HeaderButtonContainer>
      </PageHeader>
      <PageWrapper>
        <Container>
          <FilterContainer title="">
            <RangePicker
              defaultValue={[startDate, endDate]}
              value={[startDate, endDate]}
              onChange={handleDateChange}
              format="DD-MM-YYYY"
            />
          </FilterContainer>
          {isLoadingMarChart ? (
            <Spinner />
          ) : (
            <div ref={pdfContentRef}>
              <TableContainer>
                <TableHeader>
                  <Legend testId={testId} />
                </TableHeader>
                <MedicationsTable
                  startDate={startDate}
                  endDate={endDate}
                  type={medicationType}
                  residentFirstName={resident?.name?.first || ''}
                  onCellClick={handleCellClick}
                  data={tableData}
                />
              </TableContainer>
            </div>
          )}
          <RecordsSection
            records={records}
            medicationName={medicationPattern?.medicationName || ''}
            residentFirstName={resident?.name?.first || ''}
            residentFullName={
              resident ? `${resident.name.first} ${resident.name.last}` : ''
            }
            onViewRecord={handleViewRecord}
            onViewAdministrationRecord={handleViewAdministrationRecord}
            onViewDiscontinuedRecord={handleViewDiscontinuedRecord}
          />
        </Container>
        {stockCheckModalOpen && (
          <StockCheckModal
            open={stockCheckModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleStockCheckCancel}
            onSuccess={handleStockCheckSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
            trackStock={medicationPattern?.trackStock}
          />
        )}
        {addStockModalOpen && (
          <AddStockModal
            open={addStockModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleAddStockCancel}
            onSuccess={handleAddStockSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {disposalModalOpen && (
          <DisposalModal
            open={disposalModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            expectedStock={expectedStock}
            residentId={residentId || ''}
            residentMedicationId={medicationId || ''}
            unit={
              medicationPattern?.stockQuantityType || currentStock?.unit || ''
            }
            onCancel={handleDisposalCancel}
            onSuccess={handleDisposalSuccess}
            editMode={isEditMode}
            record={isEditMode ? editingRecord || undefined : undefined}
          />
        )}
        {administrationModalOpen && (
          <AdministrationModal
            open={administrationModalOpen}
            medicationName={medicationPattern?.medicationName || ''}
            record={editingRecord || undefined}
            onCancel={handleEditAdministrationCancel}
            onSuccess={handleEditAdministrationSuccess}
          />
        )}
        <StockRecordDrawer
          trackStock={medicationPattern?.trackStock}
          isVisible={drawerOpen}
          onClose={handleCloseDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
        />
        <AdministrationRecordDrawer
          isVisible={administrationDrawerOpen}
          onClose={handleCloseAdministrationDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
        />
        <AdministrationRecordDrawer
          isVisible={cellDrawerOpen}
          onClose={handleCloseCellDrawer}
          record={cellDrawerRecord}
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
          onEdit={handleEditFromDrawer}
        />
        <DiscontinuedRecordDrawer
          isVisible={discontinuedDrawerOpen}
          onClose={handleCloseDiscontinuedDrawer}
          record={
            selectedRecordId
              ? records.find((r) => r.id === selectedRecordId)
              : undefined
          }
          medicationName={medicationPattern?.medicationName || ''}
          controlledDrug={medicationPattern?.controlledDrug || false}
        />
      </PageWrapper>
    </>
  )
}

const HeaderTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const HeaderButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`

const ReadMoreLink = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.color.category.clinicalCare};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const TableContainer = styled.div`
  margin-top: 24px;
`

const TableHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`

const HeadingStyled = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  margin-right: 20px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const MedicationTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const InfoText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

export default MedicationInformationScreenV2
